import React, { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import {
  CalendarIcon,
  CloseIcon,
  EditIcon,
  DeleteIcon,
  InfoIcon,
  PlusIcon,
} from "../../../../Icons";
import InputDatePicker from "../../../EvaluationNew/components/InputDatePicker";
import { displayDateUS } from "../../../../utils/dateUtils";
import { PATIENT_THEME } from "../../patientTheme";
import useConfirm from "../../../../hooks/useConfirm";
import {
  FREQUENCY_DISCIPLINES,
  DISCIPLINES,
  filterStaffByDiscipline,
  getStaffEmployeeId,
  getRolesForDiscipline,
  defaultAssignedFormForStaff,
} from "./scheduleConstants";
import {
  durationEndDate,
  expandFrequencyVisitDates,
  daysLeftInEpisode,
  parseDateOnly,
  formatDateOnly,
  defaultVisitDays,
  uniqueVisitDays,
  visitDaysMatchFrequency,
  findDuplicateStaffFrequency,
} from "./visitFrequency";
import {
  createVisitFrequency,
  updateVisitFrequency,
  deleteVisitFrequency,
} from "../../../../services/patientService";

const inputCls =
  "box-border w-full h-9 min-h-9 px-2.5 py-0 text-sm border border-indigo-100 rounded-md bg-white focus:border-indigo-300 focus:ring-1 focus:ring-indigo-100 outline-none";
const labelCls = "block h-4 text-[11px] font-semibold leading-4 text-indigo-800 mb-1";
const fieldCls = "flex min-w-0 flex-col justify-end";
const WEEKDAYS = [
  { value: 0, label: "Sun" },
  { value: 1, label: "Mon" },
  { value: 2, label: "Tue" },
  { value: 3, label: "Wed" },
  { value: 4, label: "Thu" },
  { value: 5, label: "Fri" },
  { value: 6, label: "Sat" },
];
const TIMES = [1, 2, 3, 4, 5, 6, 7];
const WEEKS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

const emptyDraft = (episodeStart, episodeEnd) => ({
  discipline: "RN",
  staffId: "",
  timesPerWeek: 1,
  durationWeeks: 1,
  startDate: episodeStart || "",
  endDate: durationEndDate(episodeStart, 1) || episodeEnd || "",
  useDefaultDays: true,
  visitDays: [],
});

const staffDisplayName = (s) =>
  s.name || `${s.firstName || ""} ${s.lastName || ""}`.trim() || "Staff";

const clampToEpisode = (date, start, end) => {
  const d = parseDateOnly(date);
  const s = parseDateOnly(start);
  const e = parseDateOnly(end);
  if (!d) return date || "";
  if (s && d.getTime() < s.getTime()) return formatDateOnly(s);
  if (e && d.getTime() > e.getTime()) return formatDateOnly(e);
  return formatDateOnly(d);
};

const ViewFrequencyModal = ({
  open,
  onClose,
  patientId,
  patient,
  episodeMeta,
  assignedStaff = [],
  frequencies = [],
  canEdit = false,
  onChanged,
}) => {
  const { confirm, confirmDialog } = useConfirm();
  const [draft, setDraft] = useState(() =>
    emptyDraft(episodeMeta?.startDate, episodeMeta?.endDate),
  );
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const episodeStart = episodeMeta?.startDate
    ? String(episodeMeta.startDate).slice(0, 10)
    : "";
  const episodeEnd = episodeMeta?.endDate
    ? String(episodeMeta.endDate).slice(0, 10)
    : "";

  useEffect(() => {
    if (!open) return;
    setEditingId(null);
    setDraft(emptyDraft(episodeStart, episodeEnd));
  }, [open, episodeStart, episodeEnd]);

  const disciplineStaff = useMemo(
    () => filterStaffByDiscipline(assignedStaff, draft.discipline),
    [assignedStaff, draft.discipline],
  );
  const frequencyDisciplineOptions = useMemo(() => {
    const current = String(draft.discipline || "").toUpperCase();
    if (
      current &&
      !FREQUENCY_DISCIPLINES.some((d) => d.value === current)
    ) {
      const legacy = DISCIPLINES.find((d) => d.value === current);
      return [
        ...FREQUENCY_DISCIPLINES,
        legacy || { value: current, label: current },
      ];
    }
    return FREQUENCY_DISCIPLINES;
  }, [draft.discipline]);
  const roleHint = useMemo(
    () => getRolesForDiscipline(draft.discipline).join(" / ") || draft.discipline,
    [draft.discipline],
  );

  const selectedStaff = disciplineStaff.find(
    (s) => String(getStaffEmployeeId(s)) === String(draft.staffId),
  );
  const assignedForm = defaultAssignedFormForStaff(
    draft.discipline,
    selectedStaff?.role,
  );

  useEffect(() => {
    if (draft.staffId) {
      const stillValid = disciplineStaff.some(
        (s) => String(getStaffEmployeeId(s)) === String(draft.staffId),
      );
      if (!stillValid) setDraft((prev) => ({ ...prev, staffId: "" }));
      return;
    }
    if (disciplineStaff.length === 1) {
      const empId = getStaffEmployeeId(disciplineStaff[0]);
      if (empId) setDraft((prev) => ({ ...prev, staffId: String(empId) }));
    }
  }, [draft.staffId, disciplineStaff]);

  const previewDates = useMemo(
    () =>
      expandFrequencyVisitDates({
        startDate: draft.startDate,
        endDate: draft.endDate,
        timesPerWeek: draft.timesPerWeek,
        durationWeeks: draft.durationWeeks,
        useDefaultDays: draft.useDefaultDays,
        visitDays: draft.visitDays,
      }),
    [draft],
  );

  const daysLeft = daysLeftInEpisode(episodeEnd, draft.startDate);
  const set = (key, value) => setDraft((prev) => ({ ...prev, [key]: value }));
  const setStartOrDuration = (key, value) => {
    setDraft((prev) => {
      const next = { ...prev, [key]: value };
      next.endDate = clampToEpisode(
        durationEndDate(next.startDate, next.durationWeeks),
        episodeStart,
        episodeEnd,
      );
      if (key === "timesPerWeek") {
        next.visitDays = uniqueVisitDays(next.visitDays).slice(
          0,
          Number(next.timesPerWeek) || 1,
        );
      }
      if (next.useDefaultDays) {
        next.visitDays = defaultVisitDays(next.startDate, next.timesPerWeek);
      }
      return next;
    });
  };

  const toggleVisitDay = (day) => {
    if (draft.useDefaultDays) return;
    const times = Number(draft.timesPerWeek) || 1;
    const current = uniqueVisitDays(draft.visitDays);
    if (current.includes(day)) {
      set("visitDays", current.filter((x) => x !== day));
      return;
    }
    if (current.length >= times) {
      toast.error(
        `${times} x Week allows only ${times} visit day${times === 1 ? "" : "s"}`,
      );
      return;
    }
    set("visitDays", [...current, day]);
  };

  const loadPlanIntoDraft = (plan) => {
    const times = plan.timesPerWeek || 1;
    const useDefaultDays = plan.useDefaultDays !== false;
    setEditingId(plan.id);
    setDraft({
      discipline: plan.discipline || "RN",
      staffId: plan.staffId ? String(plan.staffId) : "",
      timesPerWeek: times,
      durationWeeks: plan.durationWeeks || 1,
      startDate: String(plan.startDate || "").slice(0, 10),
      endDate: String(plan.endDate || "").slice(0, 10),
      useDefaultDays,
      visitDays: useDefaultDays
        ? defaultVisitDays(plan.startDate, times)
        : uniqueVisitDays(plan.visitDays).slice(0, times),
    });
  };

  const handleAddOrSave = async () => {
    if (!canEdit) return;
    if (!disciplineStaff.length) {
      toast.error(
        `No ${roleHint} staff assigned to this patient. Assign them under Staff Assignment first.`,
      );
      return;
    }
    if (!draft.staffId) {
      toast.error(`Select assigned ${roleHint} staff for this frequency`);
      return;
    }
    if (!draft.startDate || !draft.endDate) {
      toast.error("Start and end dates are required");
      return;
    }
    if (!previewDates.length) {
      toast.error("No visits fit this frequency inside the episode dates");
      return;
    }
    if (
      !draft.useDefaultDays &&
      !visitDaysMatchFrequency(draft.visitDays, draft.timesPerWeek)
    ) {
      toast.error(
        `Select ${draft.timesPerWeek} visit day${
          draft.timesPerWeek === 1 ? "" : "s"
        } to match ${draft.timesPerWeek} x Week`,
      );
      return;
    }
    const duplicate = findDuplicateStaffFrequency(frequencies, {
      episodeId: episodeMeta?.id,
      discipline: draft.discipline,
      staffId: draft.staffId,
      startDate: draft.startDate,
      endDate: draft.endDate,
      excludeId: editingId,
    });
    if (duplicate) {
      toast.error(
        `This ${draft.discipline} employee already has a frequency from ${displayDateUS(duplicate.startDate)} to ${displayDateUS(duplicate.endDate)}. Edit that frequency instead of adding another.`,
      );
      return;
    }
    setSaving(true);
    try {
      const payload = {
        episodeId: episodeMeta?.id,
        discipline: draft.discipline,
        staffId: Number(draft.staffId),
        timesPerWeek: Number(draft.timesPerWeek),
        durationWeeks: Number(draft.durationWeeks),
        startDate: draft.startDate,
        endDate: draft.endDate,
        useDefaultDays: draft.useDefaultDays,
        visitDays: uniqueVisitDays(draft.visitDays),
        formType: assignedForm.formType,
      };
      if (editingId) {
        await updateVisitFrequency(patientId, editingId, payload);
        toast.success("Frequency updated — unfilled visits were rescheduled");
      } else {
        await createVisitFrequency(patientId, payload);
        toast.success(
          `${previewDates.length} visit${previewDates.length === 1 ? "" : "s"} scheduled with assigned forms`,
        );
      }
      setEditingId(null);
      setDraft(emptyDraft(episodeStart, episodeEnd));
      onChanged?.();
    } catch (err) {
      toast.error(err?.message || "Failed to save frequency");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (plan) => {
    if (!plan.canDelete) {
      toast.error("You can only delete frequencies you added");
      return;
    }
    const unfilled = Math.max(0, (plan.visitCount || 0) - (plan.filledCount || 0));
    const ok = await confirm({
      title: "Delete frequency?",
      message:
        unfilled > 0
          ? `This removes ${unfilled} unfilled scheduled visit(s). Filled forms stay on Eval & Orders and the calendar.`
          : "Filled forms stay on Eval & Orders and the calendar. Only the frequency plan is removed.",
    });
    if (!ok) return;
    setDeletingId(plan.id);
    try {
      await deleteVisitFrequency(patientId, plan.id);
      toast.success("Frequency deleted");
      if (editingId === plan.id) {
        setEditingId(null);
        setDraft(emptyDraft(episodeStart, episodeEnd));
      }
      onChanged?.();
    } catch (err) {
      toast.error(err?.message || "Failed to delete frequency");
    } finally {
      setDeletingId(null);
    }
  };

  if (!open) return null;

  const patientLabel = patient
    ? `Patient: ${[patient.firstName, patient.lastName].filter(Boolean).join(" ") || patient.name || "—"}${
        patient.mrn ? ` (MRN: ${patient.mrn})` : ""
      }`
    : "Patient";
  const episodeLabel = episodeMeta
    ? `Episode: ${displayDateUS(episodeMeta.startDate)} – ${displayDateUS(episodeMeta.endDate)}`
    : "";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40">
      <div
        role="dialog"
        aria-modal="true"
        className="w-full max-w-6xl max-h-[92vh] overflow-hidden rounded-xl border border-indigo-100 bg-white shadow-xl flex flex-col"
      >
        <div
          className={`flex items-center justify-between gap-3 px-5 py-3 border-b ${PATIENT_THEME.modalHead}`}
        >
          <h3 className={`text-sm font-semibold flex items-center gap-2 ${PATIENT_THEME.modalTitle}`}>
            <CalendarIcon size={16} />
            View Frequency
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-7 w-7 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100"
            aria-label="Close"
          >
            <CloseIcon size={14} />
          </button>
        </div>

        <div className="overflow-y-auto p-5 space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-indigo-100 bg-indigo-50/60 px-3 py-2">
            <p className="text-xs font-semibold text-indigo-900">{patientLabel}</p>
            <p className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-indigo-700">
              <CalendarIcon size={12} />
              {episodeLabel}
            </p>
          </div>

          {canEdit && (
            <div className="rounded-lg border border-indigo-100 p-3 space-y-3">
              <div className="overflow-x-auto">
                <div className="grid min-w-[980px] grid-cols-[6.75rem_7.25rem_6.5rem_8.5rem_8.5rem_minmax(9.25rem,1fr)_5.5rem] gap-2 items-end">
                <div className={fieldCls}>
                  <label className={labelCls}>Discipline</label>
                  <select
                    value={draft.discipline}
                    onChange={(e) =>
                      setDraft((prev) => ({
                        ...prev,
                        discipline: e.target.value,
                        staffId: "",
                      }))
                    }
                    className={inputCls}
                  >
                    {frequencyDisciplineOptions.map((d) => (
                      <option key={d.value} value={d.value}>
                        {d.value}
                      </option>
                    ))}
                  </select>
                </div>
                <div className={fieldCls}>
                  <label className={labelCls}>Frequency</label>
                  <select
                    value={draft.timesPerWeek}
                    onChange={(e) => setStartOrDuration("timesPerWeek", Number(e.target.value))}
                    className={inputCls}
                  >
                    {TIMES.map((n) => (
                      <option key={n} value={n}>
                        {n} x Week
                      </option>
                    ))}
                  </select>
                </div>
                <div className={fieldCls}>
                  <label className={labelCls}>Duration</label>
                  <select
                    value={draft.durationWeeks}
                    onChange={(e) => setStartOrDuration("durationWeeks", Number(e.target.value))}
                    className={inputCls}
                  >
                    {WEEKS.map((n) => (
                      <option key={n} value={n}>
                        {n} Week{n === 1 ? "" : "s"}
                      </option>
                    ))}
                  </select>
                </div>
                <div className={fieldCls}>
                  <label className={labelCls}>Start Date</label>
                  <InputDatePicker
                    name="freqStart"
                    formatSeparator="/"
                    allowManualEntry
                    compact
                    value={draft.startDate}
                    onChange={(e) =>
                      setStartOrDuration(
                        "startDate",
                        clampToEpisode(e.target.value, episodeStart, episodeEnd),
                      )
                    }
                    inputClassName={inputCls}
                  />
                </div>
                <div className={fieldCls}>
                  <label className={labelCls}>End Date</label>
                  <InputDatePicker
                    name="freqEnd"
                    formatSeparator="/"
                    allowManualEntry
                    compact
                    value={draft.endDate}
                    onChange={(e) =>
                      set("endDate", clampToEpisode(e.target.value, episodeStart, episodeEnd))
                    }
                    inputClassName={inputCls}
                  />
                </div>
                <div className={fieldCls}>
                  <label className={labelCls}>Staff</label>
                  <select
                    value={draft.staffId}
                    onChange={(e) => set("staffId", e.target.value)}
                    title={
                      disciplineStaff.length
                        ? undefined
                        : `No ${roleHint} assigned — add them under Staff Assignment`
                    }
                    className={`${inputCls} truncate`}
                  >
                    <option value="">Select Staff</option>
                    {disciplineStaff.map((s) => {
                      const empId = getStaffEmployeeId(s);
                      return (
                        <option key={`${s.role}-${empId}`} value={String(empId)}>
                          {staffDisplayName(s)}
                          {s.role ? ` (${s.role})` : ""}
                        </option>
                      );
                    })}
                  </select>
                </div>
                <div className={fieldCls}>
                  <span className="block h-4 mb-1" aria-hidden="true" />
                  <button
                    type="button"
                    onClick={handleAddOrSave}
                    disabled={saving || !disciplineStaff.length}
                    className="w-full h-9 min-h-9 inline-flex items-center justify-center gap-1 rounded-md bg-indigo-600 text-white text-xs font-semibold hover:bg-indigo-700 disabled:opacity-50"
                  >
                    <PlusIcon size={12} />
                    {saving ? "Saving…" : editingId ? "Update" : "Add"}
                  </button>
                </div>
              </div>
              </div>

              <div className="flex flex-wrap items-center gap-x-3 gap-y-2 min-h-9">
                <p className="text-[11px] font-semibold text-indigo-800 inline-flex items-center gap-1 shrink-0">
                  Visit Days Based On Full Week
                  <InfoIcon size={12} className="text-indigo-400" />
                </p>
                <label className="inline-flex items-center gap-1.5 text-[11px] text-slate-600">
                  <input
                    type="checkbox"
                    checked={draft.useDefaultDays}
                    onChange={(e) => {
                      const useDefaultDays = e.target.checked;
                      setDraft((prev) => {
                        const times = Number(prev.timesPerWeek) || 1;
                        const fallback = defaultVisitDays(prev.startDate, times);
                        const kept = uniqueVisitDays(prev.visitDays).slice(0, times);
                        return {
                          ...prev,
                          useDefaultDays,
                          visitDays: useDefaultDays
                            ? fallback
                            : kept.length
                              ? kept
                              : fallback,
                        };
                      });
                    }}
                  />
                  Default
                </label>
                <div className="inline-flex rounded-md border border-indigo-100 overflow-hidden">
                  {WEEKDAYS.map((d) => {
                    const selectedDays = draft.useDefaultDays
                      ? defaultVisitDays(draft.startDate, draft.timesPerWeek)
                      : uniqueVisitDays(draft.visitDays);
                    const on = selectedDays.includes(d.value);
                    const atCap =
                      !draft.useDefaultDays &&
                      !on &&
                      selectedDays.length >= (Number(draft.timesPerWeek) || 1);
                    return (
                      <button
                        key={d.value}
                        type="button"
                        disabled={draft.useDefaultDays}
                        title={
                          atCap
                            ? `${draft.timesPerWeek} x Week allows only ${draft.timesPerWeek} visit days`
                            : undefined
                        }
                        onClick={() => toggleVisitDay(d.value)}
                        className={`h-7 px-2.5 text-[11px] font-medium border-r border-indigo-100 last:border-r-0 ${
                          on
                            ? "bg-indigo-600 text-white"
                            : "bg-white text-indigo-700"
                        } disabled:opacity-70 ${atCap ? "opacity-40 cursor-not-allowed" : ""}`}
                      >
                        {d.label}
                      </button>
                    );
                  })}
                </div>
                <p className="ml-auto text-[11px] text-indigo-500 text-right">
                  Days Left To Schedule: {daysLeft} · {previewDates.length} visit
                  {previewDates.length === 1 ? "" : "s"} · Assigned{" "}
                  {assignedForm.formKind === "daily_log" ? "Daily Log" : "Visit Note"}
                </p>
              </div>
            </div>
          )}

          <div className="rounded-lg border border-indigo-100 overflow-hidden">
            <div className="flex items-center justify-between px-3 py-2 bg-indigo-50/70 border-b border-indigo-100">
              <p className="text-[11px] font-semibold text-indigo-800 inline-flex items-center gap-1">
                Frequencies below indicate visits plotted on calendar
                <InfoIcon size={12} className="text-indigo-400" />
              </p>
              <p className="text-[11px] text-indigo-600">
                Episode: #{episodeMeta?.episodeNumber || "—"}{" "}
                {displayDateUS(episodeMeta?.startDate)} – {displayDateUS(episodeMeta?.endDate)}
              </p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead className="bg-white text-indigo-700">
                  <tr>
                    {[
                      "Discipline",
                      "Frequency",
                      "Duration",
                      "No of Visits",
                      "Start Date",
                      "End Date",
                      "Staff",
                      "Action",
                    ].map((h) => (
                      <th key={h} className="px-3 py-2 font-semibold whitespace-nowrap">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(frequencies || []).length === 0 ? (
                    <tr>
                      <td colSpan={8} className="px-3 py-6 text-center text-slate-400">
                        No frequencies yet. Add one to plot visits on the calendar.
                      </td>
                    </tr>
                  ) : (
                    frequencies.map((plan) => (
                      <tr key={plan.id} className="border-t border-indigo-50">
                        <td className="px-3 py-2">{plan.discipline}</td>
                        <td className="px-3 py-2">{plan.timesPerWeek} x Week</td>
                        <td className="px-3 py-2">{plan.durationWeeks} Week(s)</td>
                        <td className="px-3 py-2">
                          {plan.visitCount}
                          <span className="ml-1 text-[10px] text-slate-400">
                            ({plan.filledCount} filled)
                          </span>
                        </td>
                        <td className="px-3 py-2">{displayDateUS(plan.startDate)}</td>
                        <td className="px-3 py-2">{displayDateUS(plan.endDate)}</td>
                        <td className="px-3 py-2">{plan.staffName || "—"}</td>
                        <td className="px-3 py-2">
                          <div className="inline-flex items-center gap-1">
                            {canEdit && (
                              <button
                                type="button"
                                onClick={() => loadPlanIntoDraft(plan)}
                                className="h-7 w-7 inline-flex items-center justify-center rounded-md border border-indigo-100 text-indigo-600 hover:bg-indigo-50"
                                title="Edit frequency"
                              >
                                <EditIcon size={12} />
                              </button>
                            )}
                            {plan.canDelete && (
                              <button
                                type="button"
                                onClick={() => handleDelete(plan)}
                                disabled={!plan.canDelete || deletingId === plan.id}
                                className="h-7 w-7 inline-flex items-center justify-center rounded-md border border-red-100 text-red-500 hover:bg-red-50 disabled:opacity-40"
                                title="Delete frequency"
                              >
                                <DeleteIcon size={12} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="flex justify-end px-5 py-3 border-t border-indigo-50">
          <button
            type="button"
            onClick={onClose}
            className="h-9 px-4 text-xs font-medium rounded-md border border-indigo-200 text-indigo-800 bg-white hover:bg-indigo-50"
          >
            Close
          </button>
        </div>
      </div>
      {confirmDialog}
    </div>
  );
};

export default ViewFrequencyModal;
